import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  
} from 'firebase/auth';
import {
  doc, getDoc, setDoc, serverTimestamp, updateDoc, increment,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../config/firebase';
import { USER_ROLES, USER_STATUS, AUTH_CONFIG } from '../config/constants';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // ═══════════════ LISTEN TO AUTH STATE ═══════════════
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setCurrentUser(firebaseUser);
      if (firebaseUser) {
        try {
          const profileDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (profileDoc.exists()) {
            setUserProfile({ id: profileDoc.id, ...profileDoc.data() });
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // ═══════════════ CHECK LOGIN ATTEMPTS (Lockout) ═══════════════
  const checkLoginAttempts = useCallback(async (email) => {
    const attemptsRef = doc(db, 'loginAttempts', email.toLowerCase());
    const attemptsDoc = await getDoc(attemptsRef);

    if (attemptsDoc.exists()) {
      const data = attemptsDoc.data();
      if (data.lockedUntil && data.lockedUntil.toDate() > new Date()) {
        const remainingMinutes = Math.ceil(
          (data.lockedUntil.toDate() - new Date()) / 60000
        );
        throw new Error(
          `Account locked. Try again in ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}.`
        );
      }
    }
  }, []);

  const recordFailedAttempt = useCallback(async (email) => {
    const attemptsRef = doc(db, 'loginAttempts', email.toLowerCase());
    const attemptsDoc = await getDoc(attemptsRef);

    let attempts = 1;
    if (attemptsDoc.exists()) {
      attempts = (attemptsDoc.data().count || 0) + 1;
    }

    const updateData = {
      count: attempts,
      lastAttempt: serverTimestamp(),
    };

    if (attempts >= AUTH_CONFIG.MAX_LOGIN_ATTEMPTS) {
      updateData.lockedUntil = new Date(
        Date.now() + AUTH_CONFIG.LOCKOUT_DURATION_MINUTES * 60 * 1000
      );
      updateData.count = 0; // Reset counter after lockout
    }

    await setDoc(attemptsRef, updateData, { merge: true });
    return attempts;
  }, []);

  const clearLoginAttempts = useCallback(async (email) => {
    const attemptsRef = doc(db, 'loginAttempts', email.toLowerCase());
    await setDoc(attemptsRef, { count: 0, lockedUntil: null }, { merge: true });
  }, []);

  // ═══════════════ REGISTER RIDER ═══════════════
  const registerRider = useCallback(async ({ email, password, firstName, lastName, phone, referralCode }) => {
    setAuthError(null);
    try {
      // 0. One account per phone — check BEFORE creating the account (no orphan on reject).
      const checkPhone = httpsCallable(functions, 'checkPhoneAvailable');
      const phoneCheck = await checkPhone({ phone });
      if (phoneCheck?.data && phoneCheck.data.available === false) {
        const e = new Error('That mobile number is already registered to another account.');
        e.code = 'app/phone-already-in-use';
        throw e;
      }

      // 1. Create Firebase Auth account
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

           // 2. Create Firestore user profile (Cloud Function assigns reference number)

      await setDoc(doc(db, 'users', user.uid), {
        email: email.toLowerCase(),
        firstName,
        lastName,
        phone,
        role: USER_ROLES.RIDER,
        status: USER_STATUS.PENDING,
        emailVerified: false,
        phoneVerified: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 4. Trigger Cloud Function to assign reference number + send OTP
      const assignRef = httpsCallable(functions, 'assignReferenceNumber');
      await assignRef({ userId: user.uid, role: USER_ROLES.RIDER });

      // 5. (Optional) attach referral code —” soft-fail, never blocks registration
      let referralResult = null;
      if (referralCode && referralCode.trim()) {
        try {
          const useReferral = httpsCallable(functions, 'useReferralCodeAtSignup');
          const r = await useReferral({ code: referralCode.trim().toUpperCase() });
          referralResult = r.data;
        } catch (e) {
          console.warn('Referral code attach failed (non-fatal):', e.message);
          referralResult = { success: false, reason: 'rpc_error' };
        }
      }

      const sendOTP = httpsCallable(functions, 'sendSmsOtp');
      await sendOTP({ phone, userId: user.uid });

      return { user, requiresOTP: true, referralResult };
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }, []);


  // ═══════════════ LOGIN ═══════════════
  const login = useCallback(async (email, password) => {
    setAuthError(null);
    try {
      await checkLoginAttempts(email);

      const { user } = await signInWithEmailAndPassword(auth, email, password);
      await clearLoginAttempts(email);

      // Fetch profile to check role and determine 2FA requirements
      const profileDoc = await getDoc(doc(db, 'users', user.uid));
      const profile = profileDoc.data();

      // Admin requires PIN + OTP
      if (profile?.role === USER_ROLES.ADMIN || profile?.role === USER_ROLES.SUPER_ADMIN) {
        setUserProfile({ id: profileDoc.id, ...profile });
        return { user, requiresPin: true, requiresOTP: true, role: profile?.role };
      }

      // Rider/Driver requires PIN (if they have one set)
      if (profile?.securityPin) {
        setUserProfile({ id: profileDoc.id, ...profile });
        return { user, requiresPin: true, requiresOTP: false, role: profile?.role };
      }

      // No PIN set yet (legacy accounts) —” go straight to dashboard
      await updateDoc(doc(db, 'users', user.uid), {
        lastLoginAt: serverTimestamp(),
      });

      setUserProfile({ id: profileDoc.id, ...profile });
      return { user, requiresPin: false, requiresOTP: false, role: profile?.role };

    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' ||
          err.code === 'auth/invalid-credential') {
        const attempts = await recordFailedAttempt(email);
        const remaining = AUTH_CONFIG.MAX_LOGIN_ATTEMPTS - attempts;
        if (remaining > 0) {
          setAuthError(`Invalid credentials. ${remaining} attempt${remaining > 1 ? 's' : ''} remaining.`);
        } else {
          setAuthError(`Too many failed attempts. Account locked for ${AUTH_CONFIG.LOCKOUT_DURATION_MINUTES} minutes. If you forgot your password, you can reset it now.`);
        }
      } else {
        setAuthError(err.message);
      }
      throw err;
    }
  }, [checkLoginAttempts, clearLoginAttempts, recordFailedAttempt]);

  // ═══════════════ VERIFY SMS OTP ═══════════════
  const verifySmsOtp = useCallback(async (otp) => {
    try {
      const verifyOTP = httpsCallable(functions, 'verifySmsOtp');
      const result = await verifyOTP({ otp, userId: currentUser.uid });

      if (result.data.verified) {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          phoneVerified: true,
          emailVerified: true,
          updatedAt: serverTimestamp(),
        });


        // Refresh profile
        const profileDoc = await getDoc(doc(db, 'users', currentUser.uid));
        setUserProfile({ id: profileDoc.id, ...profileDoc.data() });
        return true;
      }
      return false;
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }, [currentUser]);

  // ═══════════════ VERIFY ADMIN PIN ═══════════════
  const verifyAdminPin = useCallback(async (pin) => {
    try {
      const verifyPin = httpsCallable(functions, 'verifyAdminPin');
      const result = await verifyPin({ pin, userId: currentUser.uid });
      return result.data.verified;
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }, [currentUser]);

  // ═══════════════ RESEND OTP ═══════════════
  const resendOtp = useCallback(async () => {
    if (!currentUser || !userProfile?.phone) return;
    try {
      const sendOTP = httpsCallable(functions, 'sendSmsOtp');
      await sendOTP({ phone: userProfile.phone, userId: currentUser.uid });
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }, [currentUser, userProfile]);

  // ═══════════════ LOGOUT ═══════════════
  const logout = useCallback(async () => {
    try {
      if (currentUser) {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          lastLogoutAt: serverTimestamp(),
        });
      }
      await signOut(auth);
      setUserProfile(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  }, [currentUser]);

  // ═══════════════ REFRESH PROFILE ═══════════════
  const refreshProfile = useCallback(async () => {
    if (!currentUser) return;
    const profileDoc = await getDoc(doc(db, 'users', currentUser.uid));
    if (profileDoc.exists()) {
      setUserProfile({ id: profileDoc.id, ...profileDoc.data() });
    }
  }, [currentUser]);

  const value = {
    currentUser,
    userProfile,
    loading,
    authError,
    setAuthError,
    registerRider,
    login,
    logout,
    verifySmsOtp,
    verifyAdminPin,
    resendOtp,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;